import { CopyNotify, copyText } from '@/components/copy'
import { CopyIcon, EditIcon } from '@/components/Icon'
import Dialog from '@/ui/dialog'
import { OverlayContext } from '@/ui/utils/OverlayManager'
import { Button } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import { useEditPlanner } from '../edit'
import styles from './widget.module.scss'

export function WidgetButton({
  size = 'sm',
  buttonStyle = 'primary2',
  className,
  ...props
}: Parameters<typeof Button>[0]) {
  return (
    <Button size={size} buttonStyle={buttonStyle} className={classNames(className, styles.WidgetButton)} {...props} />
  )
}

export function WidgetDialog({
  title,
  onClose,
  className,
  children
}: {
  title: string
  onClose: () => void
  children: ReactNode
  className?: string
}) {
  return (
    <Dialog modal className={styles.WidgetDialog} title={title} onClose={onClose}>
      <div className={classNames(styles.WidgetDialog__content, className)}>{children}</div>
    </Dialog>
  )
}

export function PlannerWidget({
  className,
  title,
  children,
  copyDialog,
  copyLink,
  edit,
  editTitle = `Edit ${title}`,
  actions
}: {
  className?: string
  title: string
  children: ReactNode
  copyDialog?: ReactNode
  copyLink?: string
  edit?: ReactNode
  editTitle?: string
  actions?: ReactNode
}) {
  const isEditing = useEditPlanner()
  const [showCopy, setShowCopy] = React.useState(false)
  const [showEdit, setShowEdit] = React.useState(false)
  const overlays = React.useContext(OverlayContext)
  const onCopy = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (copyLink) {
        copyText(copyLink)
        overlays.async(<CopyNotify pos={{ left: e.clientX, top: e.clientY }} />)
      } else if (copyDialog) {
        setShowCopy(true)
      }
    },
    [overlays, copyDialog, copyLink]
  )

  return (
    <div className={classNames(styles.PlannerWidget, className)}>
      <div className={styles.PlannerWidget__header}>
        {title}
        <div className={styles.PlannerWidget__left}>
          {!!(copyDialog || copyLink) && (
            <WidgetButton onClick={onCopy}>
              <CopyIcon /> <span>Copy</span>
            </WidgetButton>
          )}
        </div>
        <div className={styles.PlannerWidget__right}>
          {!!edit && isEditing && (
            <WidgetButton onClick={() => setShowEdit(true)}>
              <EditIcon /> <span>Edit</span>
            </WidgetButton>
          )}
          {actions}
        </div>
      </div>
      {children}
      {!!(showCopy && copyDialog) && (
        <WidgetDialog title={`Copy ${title}`} onClose={() => setShowCopy(false)}>
          {copyDialog}
        </WidgetDialog>
      )}
      {showEdit && (
        <WidgetDialog title={editTitle} onClose={() => setShowEdit(false)}>
          {edit}
        </WidgetDialog>
      )}
    </div>
  )
}

export function TabbedWidget({
  tabs,
  children,
  className
}: {
  tabs: { id: string; name: string }[]
  children: (tab: string) => ReactNode
  className?: string
}) {
  const [tab, setTab] = React.useState(tabs[0].id)

  return (
    <div className={classNames(styles.PlannerWidget, styles.PlannerWidget__tabbed, className)}>
      <div className={styles.PlannerWidget__header}>
        {tabs.map(({ id, name }) => (
          <div
            className={classNames(styles.PlannerWidget__tab, id === tab && styles.active)}
            onClick={() => setTab(id)}
            key={id}>
            {name}
          </div>
        ))}
      </div>
      {children(tab)}
    </div>
  )
}
